import { useState, useMemo } from 'react'
import ButtonCustom from '../../../atoms/button/ButtonCustom'
import InputCustom from '../../../atoms/input/InputCustom'
import styles from '../index.module.scss'

const LineItemTab = ({ maxRows = 10, initialRows = 2, taxPercentage = 10 }) => {
	const makeRow = () => ({
		id: crypto.randomUUID(),
		description: '',
		quantity: '',
		unitPrice: '',
	})

	const [rows, setRows] = useState(
		Array.from({ length: initialRows }, makeRow)
	)

	const addRow = () => {
		if (rows.length < maxRows) {
			setRows([...rows, makeRow()])
		}
	}

	const removeRow = (id) => {
		if (rows.length > 1) {
			setRows(rows.filter((row) => row.id !== id))
		}
	}

	const updateRow = (id, field, value) => {
		setRows((prevRows) =>
			prevRows.map((row) =>
				row.id === id ? { ...row, [field]: value } : row
			)
		)
	}

	const calculateAmount = (quantity, unitPrice) => {
		const qty = parseFloat(quantity) || 0
		const price = parseFloat(unitPrice) || 0
		return qty * price
	}

	const { subtotal, tax, total } = useMemo(() => {
		const subtotal = rows.reduce((sum, row) => {
			return sum + calculateAmount(row.quantity, row.unitPrice)
		}, 0)
		const tax = (subtotal * taxPercentage) / 100
		const total = subtotal + tax
		return { subtotal, tax, total }
	}, [rows, taxPercentage])

	return (
		<div className={styles.lineItemWrp}>
			<div className={styles.header}>
				<h3>Line Items</h3>
				<ButtonCustom
					variant="default"
					onClick={addRow}
					data-cy="addRow"
				>
					+ Add Row
				</ButtonCustom>
			</div>
			<div className={styles.lineItemGrid}>
				<div className={styles.gridHeader}>
					<div className={styles.headerCell}>Description</div>
					<div className={styles.headerCell}>Quantity</div>
					<div className={styles.headerCell}>Unit Price</div>
					<div className={styles.headerCell}>Amount</div>
					<div className={styles.headerCell}></div>
				</div>

				<div className={styles.gridBody}>
					{rows.map((row) => (
						<div key={row.id} className={styles.gridRow}>
							<div className={styles.gridCell}>
								<InputCustom
									type="text"
									value={row.description}
									placeholder="Description"
									onChange={(e) =>
										updateRow(
											row.id,
											'description',
											e.target.value
										)
									}
								/>
							</div>
							<div className={styles.gridCell}>
								<InputCustom
									type="number"
									value={row.quantity}
									placeholder="5"
									onChange={(e) =>
										updateRow(
											row.id,
											'quantity',
											e.target.value
										)
									}
								/>
							</div>
							<div className={styles.gridCell}>
								<InputCustom
									type="number"
									value={row.unitPrice}
									placeholder="10"
									onChange={(e) =>
										updateRow(
											row.id,
											'unitPrice',
											e.target.value
										)
									}
								/>
							</div>
							<div className={styles.gridCell}>
								<div className={styles.amountDisplay}>
									{calculateAmount(
										row.quantity,
										row.unitPrice
									).toFixed(0)}
								</div>
							</div>
							<div className={styles.gridCell}>
								<ButtonCustom
									variant="outlined"
									size="md"
									onClick={() => removeRow(row.id)}
									disabled={rows.length === 1}
								>
									<img
										data-cy="deleteRow"
										src="/images/trash.svg"
										alt="Delete"
									/>
								</ButtonCustom>
							</div>
						</div>
					))}
				</div>

				<div className={styles.gridFooter}>
					<div className={styles.summaryRow}>
						<span className={styles.summaryLabel}>Subtotal:</span>
						<span
							className={styles.summaryValue}
							style={{ fontWeight: '400' }}
						>
							{subtotal.toFixed(0)}
						</span>
					</div>
					<div className={styles.summaryRow}>
						<span className={styles.summaryLabel}>Tax(%):</span>
						<span
							className={styles.summaryValue}
							style={{ fontWeight: '400' }}
						>
							{tax.toFixed(0)}
						</span>
					</div>
					<div className={styles.summaryRow}>
						<span className={styles.summaryLabel}>Total:</span>
						<span className={styles.summaryValue}>
							{total.toFixed(0)}
						</span>
					</div>
				</div>
			</div>
		</div>
	)
}

export default LineItemTab
